import { NgRedux, select } from '@angular-redux/store';
import { Injectable, OnDestroy } from '@angular/core';
import appboy from '@braze/web-sdk';
import { Action, Actions, BrazeUser as BrazeUser, IAppState, User } from '@core/redux/stores';
import _ from 'lodash';
import { Observable, Subscription } from 'rxjs';
import { ProxyService } from './proxy.service';
import { ApiRoutes } from '@app/shared/models/configs/api.endpoints';
import { environment } from '@environments/environment';

@Injectable({
  providedIn: 'root'
})
export class BrazeService implements OnDestroy {
  @select(appState => appState.brazeUser) brazeUserObject: Observable<BrazeUser>;
  brazeUser: BrazeUser;
  private subscriptions = new Subscription();
  private isInitialized = false;
  constructor(private ngRedux: NgRedux<IAppState>, private proxy : ProxyService) {
    this.subscriptions.add(this.brazeUserObject.subscribe(brazeUser => {
      this.brazeUser = _.cloneDeep(brazeUser);
    }));
  }
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  // Braze must only be initialized for authenticated users, immediately before changeUser().
  // Initializing on app startup would create a device-only profile for every anonymous visitor.
  private initializeBraze() {
    if (this.isInitialized) {
      return;
    }
    appboy.initialize(environment.braze.API_KEY, {
      baseUrl: environment.braze.SDK_ENDPOINT,
      enableLogging: true,
      serviceWorkerLocation: '/service-worker.js',
      safariWebsitePushId: environment.braze.SAFARI_WEBSITE_PUSH_ID
    });
    this.isInitialized = true;
  }

  public updateUser(user: User) {
    if (user?.id) {
      this.initializeBraze();
      setTimeout(() => {
        appboy.changeUser(user?.id);
        this.updateUserProperties(user);
      }, 100);
      if (this.isSafari()) {
        // We dont even need to prime the user because they have denied last soft push prompt
        if (!(user?.isSafariPushDenied === true)) {
          appboy.subscribeToInAppMessage(function (inAppMessage) {
            var shouldDisplay = true;
            if (inAppMessage instanceof appboy.InAppMessage) {
              // Read the key-value pair for msg-id
              var msgId = inAppMessage.extras["msg-id"];
              // If this is our push primer message
              if (msgId == "push-primer") {
                // We don't want to display the soft push prompt to users on browsers that don't support push, or if the user
                // has already granted/blocked permission
                if (!appboy.isPushSupported() || appboy.isPushPermissionGranted() || appboy.isPushBlocked() || (user.isSafariPushDenied === true)) {
                  shouldDisplay = false;
                }
                if ((inAppMessage as any)?.buttons[0] != null) {
                  // Prompt the user when the first button is clicked
                  (inAppMessage as any).buttons[0].subscribeToClickedEvent(function () {
                    //Accept PUSH
                    const safariUser = _.cloneDeep(user);
                    safariUser.isSafariPushDenied = false;
                    appboy.registerAppboyPushMessages();
                    this.ngRedux.dispatch({ type: Actions.UPDATEUSERPROFILE, data: safariUser });
                  }.bind(this));
                }
                if ((inAppMessage as any)?.buttons[1] != null) {
                  (inAppMessage as any).buttons[1].subscribeToClickedEvent(function () {
                    //Deny PUSH
                    const safariUser = _.cloneDeep(user);
                    // This doesnt do anything in Safari. but just in case
                    appboy.unregisterAppboyPushMessages();
                    // Set this flag in User profile
                    safariUser.isSafariPushDenied = true;
                    this.ngRedux.dispatch({ type: Actions.UPDATEUSERPROFILE, data: safariUser });
                  }.bind(this));
                }
              }
            }
            // Display the message
            if (shouldDisplay) {
              appboy.display.showInAppMessage(inAppMessage);
            }
          }.bind(this));
        }
        appboy.openSession();
        // We dont even need to prime the user because they have denied last soft push prompt
        if (!(user?.isSafariPushDenied === true)) {
          this.sendBrazeCustomEvent("prime-for-push");
        }
      } else {
        appboy.display.automaticallyShowNewInAppMessages();
        appboy.registerAppboyPushMessages();
        appboy.openSession();
      }
    }
  }
  private updateUserProperties(user: User) {    
    appboy.getUser()?.getUserId(currentUserId => {
      if (currentUserId) {
        if(!this.brazeUser) {
          return;
        }
        let doesBrazeUserNeedUpdate = false;
        if (currentUserId === user?.id) {
          if (user?.email && user?.email !== this.brazeUser.users[0]?.email) {
            appboy.getUser().setEmail(user.email);
            if(this.brazeUser?.users?.length) {
              this.brazeUser.users[0].email = user.email;
            }
            doesBrazeUserNeedUpdate = true;
          }
          if (user?.firstName && user?.firstName !== this.brazeUser.users[0]?.first_name) {
            appboy.getUser().setFirstName(user.firstName);
            if(this.brazeUser?.users?.length) {
              this.brazeUser.users[0].first_name = user.firstName;
            }
            doesBrazeUserNeedUpdate = true;
          }
          if (user?.lastName && user?.lastName !== this.brazeUser.users[0]?.last_name) {
            appboy.getUser().setLastName(user.lastName);
            if(this.brazeUser?.users?.length) {
              this.brazeUser.users[0].last_name = user.lastName;
            }
            doesBrazeUserNeedUpdate = true;
          }
          if (user?.phoneNumber && user?.phoneNumber !== this.brazeUser.users[0]?.phone) {
            appboy.getUser().setPhoneNumber(user.phoneNumber);
            if(this.brazeUser?.users?.length) {
              this.brazeUser.users[0].phone = user.phoneNumber;
            }
            doesBrazeUserNeedUpdate = true;
          }
          if ((user?.zipCode ?? '') !== (this.brazeUser.users[0]?.home_city ?? '')) {
            appboy.getUser().setHomeCity(user.zipCode ?? '');
            if(this.brazeUser?.users?.length) {
              this.brazeUser.users[0].home_city = user.zipCode ?? '';
            }
            doesBrazeUserNeedUpdate = true;
          }
          if(doesBrazeUserNeedUpdate) { 
            if(this.brazeUser?.users?.length) {
              this.ngRedux.dispatch({type: Actions.BRAZEUSER, data: this.brazeUser});
            } else {
              this.proxy.httpGet(ApiRoutes.BRAZE_USERS).subscribe(
                success => {
                  var brazeUser = <BrazeUser>success.body.valueOf();
                  this.ngRedux.dispatch({type: Actions.BRAZEUSER, data: brazeUser});
                  this.brazeUser = brazeUser;
                });
            }
          }
        }
      }
    });
  }

  public sendBrazeCustomEvent(eventName: string, properties: any = null) {
    appboy.logCustomEvent(eventName, properties);
  }  
  private isSafari() : boolean {
    return  !!navigator.userAgent.match(/Version\/[\d\.]+.*Safari/);
  }
}
